import {
	coordKey,
	getDirection,
	getNeighbors,
	type HexDirection,
} from "../../hex-path/hex-grid";
import type { HexCoord } from "../../hex-path/types";

export const THUMB_MOVEMENT_DIRECTIONS = [
	"up",
	"upRight",
	"right",
	"downRight",
	"down",
	"downLeft",
	"left",
	"upLeft",
] as const;

export type ThumbMovementDirection = (typeof THUMB_MOVEMENT_DIRECTIONS)[number];

/** Screen-space unit vectors for the 8 thumb sectors (0° = right, clockwise). */
export const THUMB_MOVEMENT_DIRECTION_VECTORS: Record<
	ThumbMovementDirection,
	{ x: number; y: number }
> = {
	up: { x: 0, y: -1 },
	upRight: { x: 1, y: -1 },
	right: { x: 1, y: 0 },
	downRight: { x: 1, y: 1 },
	down: { x: 0, y: 1 },
	downLeft: { x: -1, y: 1 },
	left: { x: -1, y: 0 },
	upLeft: { x: -1, y: -1 },
};

/**
 * Resolve drag vector to the nearest of 8 thumb directions for UI highlighting.
 * Movement hex resolution is handled separately via resolveHexDirectionFromThumbDirection.
 */
export function resolveThumbDirectionFromVector(
	dx: number,
	dy: number,
): ThumbMovementDirection | null {
	const magnitude = Math.hypot(dx, dy);
	if (magnitude < 0.001) {
		return null;
	}

	let angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
	if (angleDeg < 0) {
		angleDeg += 360;
	}

	const sectorLabels = [
		"right",
		"downRight",
		"down",
		"downLeft",
		"left",
		"upLeft",
		"up",
		"upRight",
	] as const;
	const sectorIndex = Math.round(angleDeg / 45) % 8;
	return sectorLabels[sectorIndex] ?? "right";
}

export function resolveHexDirectionFromThumbDirection(
	direction: ThumbMovementDirection,
	options?: {
		origin?: HexCoord;
		pathCoordSet?: Set<string>;
	},
): HexDirection {
	if (direction === "up" || direction === "down") {
		const { origin, pathCoordSet } = options ?? {};
		if (origin && pathCoordSet) {
			return resolveVerticalThumbHexDirection(
				direction,
				origin,
				pathCoordSet,
			);
		}
		return direction === "up" ? "upRight" : "downRight";
	}

	switch (direction) {
		case "upLeft":
			return "upLeft";
		case "upRight":
			return "upRight";
		case "downLeft":
			return "downLeft";
		case "downRight":
			return "downRight";
		case "left":
			return "left";
		case "right":
			return "right";
		default:
			return "right";
	}
}

function resolveVerticalThumbHexDirection(
	direction: "up" | "down",
	origin: HexCoord,
	pathCoordSet: Set<string>,
): HexDirection {
	const leftOption: HexDirection = direction === "up" ? "upLeft" : "downLeft";
	const rightOption: HexDirection =
		direction === "up" ? "upRight" : "downRight";
	const hasLeft =
		resolvePathNeighborForHexDirection(origin, leftOption, pathCoordSet) !=
		null;
	const hasRight =
		resolvePathNeighborForHexDirection(origin, rightOption, pathCoordSet) !=
		null;

	if (hasLeft && !hasRight) {
		return leftOption;
	}
	return rightOption;
}

export function resolvePathNeighborForHexDirection(
	origin: HexCoord,
	hexDirection: HexDirection,
	pathCoordSet: Set<string>,
): HexCoord | null {
	for (const neighbor of getNeighbors(origin.row, origin.col)) {
		const neighborKey = coordKey(neighbor.row, neighbor.col);
		if (!pathCoordSet.has(neighborKey)) {
			continue;
		}
		if (
			getDirection(origin.row, origin.col, neighbor.row, neighbor.col) ===
			hexDirection
		) {
			return neighbor;
		}
	}
	return null;
}

/**
 * Acceptance cone for stick → hex edge (~70°). Soft-redirects near a missing
 * diagonal; outside the cone → no step (never pick an arbitrary far edge).
 */
export const THUMB_HEX_NEIGHBOR_MAX_ANGLE_RAD = (70 * Math.PI) / 180;

/**
 * Best in-cone neighbor must beat the runner-up by this margin. Pure left with
 * only upLeft+downLeft available is ambiguous → stay put (no coin-flip hop).
 */
export const THUMB_HEX_NEIGHBOR_UNIQUENESS_RAD = (22 * Math.PI) / 180;

/**
 * Hysteresis when stick angle crosses sector boundaries (degrees).
 * Keeps last locomotion intent until the stick clearly leaves the wedge.
 */
export const THUMB_LOCOMOTION_INTENT_HYSTERESIS_DEG = 12;

/**
 * Screen-space vector → best **available** path neighbor by angular distance.
 * Locomotion SoT — do not drive steps from 8-way UI sectors alone.
 *
 * @returns null when no in-cone neighbor, or when two candidates are too close
 * in angle (no direction bias)
 */
export function resolveBestPathNeighborFromScreenVector(
	origin: HexCoord,
	vector: { x: number; y: number },
	pathCoordSet: Set<string>,
	options?: {
		maxAngleRad?: number;
		/** Prefer this neighbor when still within the cone (step sticky). */
		preferNeighbor?: HexCoord | null;
		preferSlackRad?: number;
		/** Extra gate (unlock / visibility). Defaults to allow. */
		isNeighborAllowed?: (neighbor: HexCoord) => boolean;
		/** Override uniqueness margin (radians). */
		uniquenessRad?: number;
	},
): HexCoord | null {
	const len = Math.hypot(vector.x, vector.y);
	if (len < 1e-6) {
		return null;
	}
	const stickAngle = Math.atan2(vector.y, vector.x);
	const maxAngle = options?.maxAngleRad ?? THUMB_HEX_NEIGHBOR_MAX_ANGLE_RAD;
	const uniquenessRad =
		options?.uniquenessRad ?? THUMB_HEX_NEIGHBOR_UNIQUENESS_RAD;
	const prefer = options?.preferNeighbor ?? null;
	const preferSlack = options?.preferSlackRad ?? 0.2;
	const isAllowed = options?.isNeighborAllowed ?? (() => true);

	let bestInCone: HexCoord | null = null;
	let bestInConeDelta = Number.POSITIVE_INFINITY;
	let secondInConeDelta = Number.POSITIVE_INFINITY;

	for (const neighbor of getNeighbors(origin.row, origin.col)) {
		const neighborKey = coordKey(neighbor.row, neighbor.col);
		if (!pathCoordSet.has(neighborKey)) {
			continue;
		}
		if (!isAllowed(neighbor)) {
			continue;
		}
		const hexDir = getDirection(
			origin.row,
			origin.col,
			neighbor.row,
			neighbor.col,
		);
		const screen = THUMB_MOVEMENT_DIRECTION_VECTORS[hexDir];
		const neighborAngle = Math.atan2(screen.y, screen.x);
		let delta = Math.abs(neighborAngle - stickAngle);
		if (delta > Math.PI) {
			delta = Math.PI * 2 - delta;
		}
		if (
			prefer &&
			prefer.row === neighbor.row &&
			prefer.col === neighbor.col
		) {
			delta = Math.max(0, delta - preferSlack);
		}
		if (delta > maxAngle) {
			continue;
		}
		if (delta < bestInConeDelta) {
			secondInConeDelta = bestInConeDelta;
			bestInConeDelta = delta;
			bestInCone = neighbor;
		} else if (delta < secondInConeDelta) {
			secondInConeDelta = delta;
		}
	}

	if (!bestInCone) {
		return null;
	}
	/** Sole in-cone candidate — soft-redirect is unambiguous. */
	if (!Number.isFinite(secondInConeDelta)) {
		return bestInCone;
	}
	/** Ambiguous choice (e.g. left between upLeft and downLeft) — stay put. */
	if (secondInConeDelta - bestInConeDelta < uniquenessRad) {
		return null;
	}
	return bestInCone;
}

/**
 * Sticky locomotion intent: keep `lastNeighbor` until stick clearly prefers
 * another available edge (hysteresis on angular score).
 */
export function resolveStickyPathNeighborFromScreenVector(
	origin: HexCoord,
	vector: { x: number; y: number },
	pathCoordSet: Set<string>,
	lastNeighbor: HexCoord | null,
	isNeighborAllowed?: (neighbor: HexCoord) => boolean,
): HexCoord | null {
	const hysteresisRad =
		(THUMB_LOCOMOTION_INTENT_HYSTERESIS_DEG * Math.PI) / 180;
	return resolveBestPathNeighborFromScreenVector(
		origin,
		vector,
		pathCoordSet,
		{
			preferNeighbor: lastNeighbor,
			preferSlackRad: hysteresisRad,
			isNeighborAllowed,
		},
	);
}

export function resolveThumbDragMagnitude(
	dx: number,
	dy: number,
	maxRadius: number,
): number {
	if (maxRadius <= 0) {
		return 0;
	}
	return Math.min(1, Math.hypot(dx, dy) / maxRadius);
}

/**
 * Inner stick deadzone (fraction of max radius / unit analog). Shared by
 * ephemeral scene stick and docked map pad.
 */
export const THUMB_ANALOG_INNER_DEADZONE = 0.12;

/**
 * Hex-map: do not *start* a new hop until the stick clears this (above the
 * inner deadzone). Prevents baking a multi-second sneak step from the first
 * post-deadzone sample while the thumb is still ramping to the rim.
 */
export const THUMB_HEX_STEP_COMMIT_DEADZONE = 0.38;

/** Floor speed just past the deadzone (crawl), before the response curve. */
export const THUMB_ANALOG_MIN_SPEED_SCALE = 0.1;

/**
 * Hex-map locomotion floor — once a hop is committed, never crawl below walk
 * band. Fine sneak remains available on scene sticks via {@link resolveAnalogSpeedScale}.
 */
export const THUMB_HEX_MAP_MIN_SPEED_SCALE = 0.5;

/**
 * Map stick deflection (0..1) → locomotion speed scale (0..1).
 * Deadzone remap + quadratic ease-in for fine control near center and full
 * speed at the rim (standard virtual-joystick response).
 */
export function resolveAnalogSpeedScale(magnitude: number): number {
	const mag = Math.min(1, Math.max(0, magnitude));
	if (mag <= THUMB_ANALOG_INNER_DEADZONE) {
		return 0;
	}
	const t =
		(mag - THUMB_ANALOG_INNER_DEADZONE) / (1 - THUMB_ANALOG_INNER_DEADZONE);
	// Quadratic ease-in: precise near center, ramps to full at the rim.
	const eased = t * t;
	return (
		THUMB_ANALOG_MIN_SPEED_SCALE +
		eased * (1 - THUMB_ANALOG_MIN_SPEED_SCALE)
	);
}

/**
 * Hex-map thumb → speed: linear after deadzone, floored at walk so rim-intent
 * cannot spend seconds in sneak from an early sample.
 */
export function resolveHexMapAnalogSpeedScale(magnitude: number): number {
	const mag = Math.min(1, Math.max(0, magnitude));
	if (mag <= THUMB_ANALOG_INNER_DEADZONE) {
		return 0;
	}
	const t =
		(mag - THUMB_ANALOG_INNER_DEADZONE) / (1 - THUMB_ANALOG_INNER_DEADZONE);
	return (
		THUMB_HEX_MAP_MIN_SPEED_SCALE + t * (1 - THUMB_HEX_MAP_MIN_SPEED_SCALE)
	);
}

/**
 * Peak-hold magnitude while the stick is held — step start uses the peak so a
 * fast flick to the rim is not stuck on the ramp sample.
 */
export function resolvePeakHeldMagnitude(
	currentMagnitude: number,
	previousPeak: number,
): number {
	const current = Math.min(1, Math.max(0, currentMagnitude));
	const peak = Math.min(1, Math.max(0, previousPeak));
	return Math.max(current, peak);
}

/**
 * Hex-map step duration from stick magnitude (faster at the rim).
 * Uses {@link resolveHexMapAnalogSpeedScale} (walk-floor, linear).
 */
export function resolveThumbStepDurationMs(
	magnitude: number,
	msPerTile: number,
): number {
	const speedScale = resolveHexMapAnalogSpeedScale(magnitude);
	if (speedScale <= 0) {
		return Math.max(16, msPerTile * 8);
	}
	return Math.max(16, Math.round(msPerTile / speedScale));
}

export function clampThumbDragVector(
	dx: number,
	dy: number,
	maxRadius: number,
): { dx: number; dy: number } {
	const magnitude = Math.hypot(dx, dy);
	if (magnitude <= maxRadius || magnitude < 0.001) {
		return { dx, dy };
	}
	const scale = maxRadius / magnitude;
	return { dx: dx * scale, dy: dy * scale };
}

/** Hex step direction maps 1:1 to thumb visuals (6 hex dirs ⊂ 8 thumb dirs). */
export function resolveThumbDirectionFromHexDirection(
	hexDirection: HexDirection,
): ThumbMovementDirection {
	return hexDirection;
}

export function resolveThumbOffsetFromDriveState(
	direction: ThumbMovementDirection,
	magnitude: number,
	maxRadius: number,
): { dx: number; dy: number } {
	const vector = THUMB_MOVEMENT_DIRECTION_VECTORS[direction];
	const unitLength = Math.hypot(vector.x, vector.y);
	if (unitLength < 0.001) {
		return { dx: 0, dy: 0 };
	}
	const radius = Math.max(0, Math.min(1, magnitude)) * maxRadius;
	return clampThumbDragVector(
		(vector.x / unitLength) * radius,
		(vector.y / unitLength) * radius,
		maxRadius,
	);
}

/** Arrow + WASD → screen vectors (same mapping as scene free loco). */
const LOCO_KEY_TO_VECTOR: Record<string, { x: number; y: number }> = {
	ArrowUp: { x: 0, y: -1 },
	ArrowDown: { x: 0, y: 1 },
	ArrowLeft: { x: -1, y: 0 },
	ArrowRight: { x: 1, y: 0 },
	w: { x: 0, y: -1 },
	a: { x: -1, y: 0 },
	s: { x: 0, y: 1 },
	d: { x: 1, y: 0 },
};

/** Normalize KeyboardEvent.key for loco set membership (WASD case-insensitive). */
export function normalizeThumbLocoKey(key: string): string | null {
	if (
		key === "ArrowUp" ||
		key === "ArrowDown" ||
		key === "ArrowLeft" ||
		key === "ArrowRight"
	) {
		return key;
	}
	const lower = key.length === 1 ? key.toLowerCase() : key;
	if (lower === "w" || lower === "a" || lower === "s" || lower === "d") {
		return lower;
	}
	return null;
}

export function resolveThumbDriveStateFromArrowKeys(
	pressedKeys: ReadonlySet<string>,
): {
	direction: ThumbMovementDirection;
	magnitude: number;
	vector: { x: number; y: number };
} | null {
	let dx = 0;
	let dy = 0;
	for (const key of pressedKeys) {
		const vector = LOCO_KEY_TO_VECTOR[key];
		if (!vector) {
			continue;
		}
		dx += vector.x;
		dy += vector.y;
	}
	if (dx === 0 && dy === 0) {
		return null;
	}
	const direction = resolveThumbDirectionFromVector(dx, dy);
	if (!direction) {
		return null;
	}
	const len = Math.hypot(dx, dy) || 1;
	return {
		direction,
		magnitude: 1,
		vector: { x: dx / len, y: dy / len },
	};
}

export const THUMB_CONTROL_MAX_DRAG_PX = 56;
